import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { browserTracingIntegration } from "@sentry/react";
import { ToastProvider } from "@swordsweeper/ssi-ui-component-library";
import ClinicDesktopPage from "@src/components/pages/ClinicDesktopPage/";
import ProductListPage from "@src/components/pages/ProductListPage";
import ProductRecommendationsPage from "@src/components/pages/ProductRecommendationsPage";
import ProductDetailPage from "@src/components/pages/ProductDetailPage";
import CartPage from "@src/components/pages/CartPage";
import CheckoutPage from "@src/components/pages/CheckoutPage";
import OrdersPage from "@src/components/pages/OrdersPage";
import OrderPlacedPage from "@src/components/pages/OrderPlacedPage";
import AccountPage from "@src/components/pages/AccountPage";
// Note: Commented out for Beta - messaging function is hidden, beta will not have messaging
// import MessagesPage from "@src/components/pages/MessagesPage";
import SupportPage from "@src/components/pages/SupportPage";
import CovetrusAuthCallbackPage from "@src/components/pages/CovetrusAuthCallbackPage";
import SearchResultsPage from "@src/components/pages/SearchResultsPage/SearchResultsPage";
import TermsUpdatePage from "@src/components/pages/TermsUpdatePage";
import SignUpPage from "@src/components/pages/SignUpPage";
import EmailSentPage from "@src/components/pages/EmailSentPage";
import ClinicSetupPage from "@src/components/pages/ClinicSetupPage";
import ClinicPendingPage from "@src/components/pages/ClinicPendingPage";
import FavoritesPage from "@src/components/pages/FavoritesPage/FavoritesPage";
import PromotedProductsPage from "@src/components/pages/PromotedProductsPage";
import ManageClinicPage from "@src/components/pages/ManageClinicPage";
import ManageClinicDetailPage from "@src/components/pages/ManageClinicDetailPage";
import ManageTeamPage from "@src/components/pages/ManageTeamPage";
import AppContainer from "@src/components/containers/AppContainer";
import RequiresPermissionContainer from "@src/components/containers/RequiresPermissionContainer";
import RequiresClinicAdminContainer from "@src/components/containers/RequiresClinicAdminContainer";
import ErrorPage from "@src/components/pages/ErrorPage";
import NotFoundPage from "@src/components/pages/NotFoundPage";
import OfflineBanner from "@src/components/widgets/OfflineBanner";
import NetworkStatusProvider from "@src/providers/NetworkStatusProvider";
import Permissions from "@src/constants/Permissions";
import PermissionLevels from "@src/constants/PermissionLevels";
import { loadPendoScript } from "@src/utils/pendo";
import RequiresRoleContainer from "./components/containers/RequiresRoleContainer";
import RequiresTermsContainer from "@src/components/containers/RequiresTermsContainer/RequiresTermsContainer";
import RequiresActiveClinicContainer from "@src/components/containers/RequiresActiveClinicContainer/RequiresActiveClinicContainer";
import Roles from "./constants/Roles";
import SimilarProductsPage from "./components/pages/SimilarProductsPage";

const isQa = process.env.NODE_ENV === "qa";
const isProd = process.env.NODE_ENV === "production";

// Load Pendo script and sentry on app initialization (qa or production only)

if (isQa || isProd) {
  loadPendoScript();
  Sentry.init({
    dsn: process.env.SENTRY_DSN,
    integrations: [browserTracingIntegration()],
    environment: process.env.ENVIRONMENT,
    tracesSampleRate: 1.0,
    debug: false,
    attachStacktrace: true,
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "No matching state found in storage",
      "Unauthorized",
      "TypeError: Cannot read properties of null (reading 'zoom')",
    ],
  });
}

const routesJsx = (
  <BrowserRouter
    basename="/app"
    future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
  >
    <Routes>
      <Route path="/terms-update" element={<TermsUpdatePage />} />
      <Route path="/sign-up" element={<SignUpPage />} />
      <Route path="/sign-up/email-sent" element={<EmailSentPage />} />
      <Route path="/clinic-setup" element={<ClinicSetupPage />} />
      <Route path="/clinic-pending" element={<ClinicPendingPage />} />
      <Route element={<AppContainer />}>
        <Route element={<RequiresActiveClinicContainer />}>
          <Route element={<RequiresTermsContainer />}>
          <Route
            path="/product-category/:productCategoryId"
            element={
              <RequiresPermissionContainer
                permission={{
                  slug: Permissions.ProductCategories,
                  level: PermissionLevels.View,
                }}
                element={<ProductListPage />}
              />
            }
          />
          <Route
            path="/product/:productId/similar-products"
            element={
              <RequiresPermissionContainer
                permission={{
                  slug: Permissions.Products,
                  level: PermissionLevels.View,
                }}
                element={<SimilarProductsPage />}
              />
            }
          />
          <Route
            path="/product/:productId"
            element={
              <RequiresPermissionContainer
                permission={{
                  slug: Permissions.Products,
                  level: PermissionLevels.View,
                }}
                element={<ProductDetailPage />}
              />
            }
          />
          <Route
            path="/product-recommendations/:type?"
            element={
              <RequiresPermissionContainer
                permission={{
                  slug: Permissions.Products,
                  level: PermissionLevels.View,
                }}
                element={<ProductRecommendationsPage />}
              />
            }
          />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route
            path="/orders"
            element={
              <RequiresPermissionContainer
                permission={{
                  slug: Permissions.Orders,
                  level: PermissionLevels.View,
                }}
                element={<OrdersPage />}
              />
            }
          />
          <Route
            path="/orders/:orderNumber"
            element={
              <RequiresPermissionContainer
                permission={{
                  slug: Permissions.Orders,
                  level: PermissionLevels.View,
                }}
                element={<OrderPlacedPage />}
              />
            }
          />
          <Route path="/account/:view?" element={<AccountPage />} />
          <Route
            path="/manage-clinic"
            element={
              <RequiresClinicAdminContainer element={<ManageClinicPage />} />
            }
          />
          <Route
            path="/manage-clinic/:clinicId"
            element={
              <RequiresClinicAdminContainer
                element={<ManageClinicDetailPage />}
              />
            }
          />
          <Route
            path="/manage-team"
            element={
              <RequiresClinicAdminContainer element={<ManageTeamPage />} />
            }
          />
          {/* Note: Commented out for Beta - messaging function is hidden, beta will not have messaging */}
          {/* <Route
          path="/messages/:messageThreadId?"
          element={<MessagesPage />}
        /> */}
          <Route path="/support" element={<SupportPage />} />
          <Route
            path="/favorites"
            element={
              <RequiresPermissionContainer
                permission={{
                  slug: Permissions.Products,
                  level: PermissionLevels.View,
                }}
                element={<FavoritesPage />}
              />
            }
          />
          <Route
            path="/promoted-products/:distributorSlug"
            element={
              <RequiresPermissionContainer
                permission={{
                  slug: Permissions.Products,
                  level: PermissionLevels.View,
                }}
                element={<PromotedProductsPage />}
              />
            }
          />
          <Route
            path="/callback/covetrus"
            element={
              <RequiresPermissionContainer
                permission={{
                  slug: Permissions.Distributors,
                  level: PermissionLevels.View,
                }}
                element={<CovetrusAuthCallbackPage />}
              />
            }
          />
          <Route
            path="/search"
            element={
              <RequiresPermissionContainer
                permission={{
                  slug: Permissions.Products,
                  level: PermissionLevels.View,
                }}
                element={<SearchResultsPage />}
              />
            }
          />
          <Route
            path=""
            element={
              <RequiresRoleContainer
                role={Roles.ClinicUser}
                element={<ClinicDesktopPage />}
              />
            }
          />
          {/* TODO: Implement role-specific home pages */}
          <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  </BrowserRouter>
);

const contextJsx = (
  <NetworkStatusProvider>
    <OfflineBanner />
    <ToastProvider>{routesJsx}</ToastProvider>
  </NetworkStatusProvider>
);

const RootApp = isProd ? (
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorPage />}>
      {contextJsx}
    </Sentry.ErrorBoundary>
  </React.StrictMode>
) : (
  contextJsx
);

const container = document.getElementById("app");
if (container) {
  createRoot(container).render(RootApp);
}
